import React from 'react';
import {
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { useTranslation } from 'react-i18next';

export type TakeoverStepState = 'pending' | 'running' | 'done' | 'error' | 'timeout';

export type TakeoverFinalAction = 'print' | 'sign';

export interface TakeoverStatus {
  finalAction: TakeoverFinalAction;
  decredo: TakeoverStepState;
  document: TakeoverStepState;
  error?: string;
}

export const isTakeoverRunning = (status: TakeoverStatus | null) =>
  !!status && [status.decredo, status.document].includes('running');

function StepStateIcon({ state }: { state: TakeoverStepState }) {
  switch (state) {
    case 'running':
      return <CircularProgress size={20} />;
    case 'done':
      return <CheckCircleIcon color="success" />;
    case 'error':
      return <ErrorIcon color="error" />;
    case 'timeout':
      return <HourglassEmptyIcon color="warning" />;
    default:
      return <Box sx={{ width: 24, height: 24 }} />;
  }
}

interface Props {
  status: TakeoverStatus | null;
}

export function TakeoverProgressPanel({ status }: Props) {
  const { t } = useTranslation();

  if (!status) {
    return (
      <Box>
        <Typography variant="subtitle1">{t('deals.takeoverStatus.title')}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t('deals.takeoverStatus.notStarted')}
        </Typography>
      </Box>
    );
  }

  const steps: { key: string; label: string; state: TakeoverStepState }[] = [
    {
      key: 'decredo',
      label: t('deals.takeoverStatus.decredo'),
      state: status.decredo,
    },
    {
      key: 'document',
      label:
        status.finalAction === 'sign'
          ? t('deals.takeoverStatus.sign')
          : t('deals.takeoverStatus.print'),
      state: status.document,
    },
  ];

  return (
    <Box>
      <Typography variant="subtitle1">{t('deals.takeoverStatus.title')}</Typography>
      <List dense>
        {steps.map((step) => (
          <ListItem key={step.key}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <StepStateIcon state={step.state} />
            </ListItemIcon>
            <ListItemText
              primary={step.label}
              primaryTypographyProps={
                step.state === 'pending' ? { color: 'text.disabled' } : undefined
              }
            />
          </ListItem>
        ))}
      </List>
      {status.decredo === 'timeout' && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2">{t('deals.takeoverStatus.timeoutHint')}</Typography>
        </Box>
      )}
      {status.error && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" color="error">
            {status.error}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
